import { useState } from 'react';
import { classNames } from 'primereact/utils';
import { InfoIcon } from '@repo/atomic-components';
import { useViewportPageIndexContext } from '../hooks';
import { BundleItemDataExtended } from '@/api';
import {
  makeSelectBundlePageDataByPageIndex,
  selectBundlePageIds,
  selectViewportPageIndexInStore,
  useAppSelector,
} from '@/store';

export const ViewPageNavigationInfo = () => {
  const documentReviewPageContext = useViewportPageIndexContext();
  const bundlePageIds = useAppSelector(selectBundlePageIds);
  const viewportPageIndexInStore = useAppSelector(selectViewportPageIndexInStore);

  const getBundlePageDataByPageIndex = useAppSelector(makeSelectBundlePageDataByPageIndex);

  const [showViewedPageInfo, setShowViewedPageInfo] = useState<boolean>(false);
  const [pageData, setPageData] = useState<BundleItemDataExtended | undefined>(undefined);

  const handleMouseEnter = () => {
    const rowPageData = getBundlePageDataByPageIndex(viewportPageIndexInStore);

    setPageData(rowPageData);
    setShowViewedPageInfo(true);
  };

  const handleMouseLeave = () => {
    setShowViewedPageInfo(false);
    setPageData(undefined);
  };

  return (
    <div className="relative">
      {showViewedPageInfo && (
        <div
          className={classNames(
            'absolute right-0 bottom-[calc(100%+8px)] z-50 w-auto',
            'p-4',
            'flex flex-col items-start justify-center gap-1',
            'rounded-[4px] border border-gray-100 bg-white text-[14px] shadow-sm',
          )}
        >
          <div
            className="text-fletchers-gray-200 w-full overflow-hidden text-[14px] font-normal text-ellipsis whitespace-nowrap"
            title={`Document: ${pageData?.documentName}`}
          >
            Document: {pageData?.documentName}
          </div>
          <div className="text-[14px] font-semibold text-black">
            Original page: {pageData?.originalPageNumber} / {pageData?.documentLength}
          </div>
        </div>
      )}

      <div
        className={classNames(
          'h-[32px] w-auto px-2',
          'flex items-center justify-center gap-1.5',
          'rounded-[4px] border border-gray-100 bg-white text-[14px] shadow-sm',
        )}
      >
        <span>Page</span>
        <input
          ref={documentReviewPageContext.pageNumberInputRef}
          name="pageNumberInput"
          type="number"
          min="1"
          max={bundlePageIds.length}
          step="1"
          data-max-digits={bundlePageIds.length.toString().length}
          defaultValue="1"
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              if (
                documentReviewPageContext.pageNumberInputRef.current &&
                documentReviewPageContext.pageNumberInputRef.current.value
              ) {
                const pageIndexFromInputValue =
                  parseInt(documentReviewPageContext.pageNumberInputRef.current.value) - 1;

                if (pageIndexFromInputValue >= 0 && pageIndexFromInputValue <= bundlePageIds.length - 1) {
                  documentReviewPageContext.onScrollToPageIndexInReactVirtualizedViewport(pageIndexFromInputValue);
                }

                if (pageIndexFromInputValue < 0) {
                  documentReviewPageContext.onScrollToPageIndexInReactVirtualizedViewport(0);
                }

                if (pageIndexFromInputValue > bundlePageIds.length - 1) {
                  documentReviewPageContext.onScrollToPageIndexInReactVirtualizedViewport(bundlePageIds.length - 1);
                }
              }
            }
          }}
          className={classNames(
            'inline-block',
            'w-16 px-1',
            'border-fletchers-gray-100 bg-fletchers-white rounded-sm border-1 focus:outline-offset-0 focus:outline-none',
          )}
        />
        <span>of</span>
        <span>{bundlePageIds.length}</span>

        <div
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          className={classNames(
            'bg-fletchers-green-500 flex h-[17px] w-[17px] items-center justify-center rounded-2xl',
          )}
        >
          <InfoIcon />
        </div>
      </div>
    </div>
  );
};
